import { useState, useCallback, useRef } from "react";
import { deBounce } from "../utils/deBounce";
import { generateValidationMessage } from "../utils/messageGenerator";
import { safelyGetRecords } from "../utils/dataHelpers";
import MainServices from "../services/MainServices";
import apiServices from "../services/ApiServices";
import useAuthContext from "./useAuthContext"; // Fixed import

export const useDebounceSearch = ({
  endpoint,
  columnName,
  statusField = "status",
  debounceMs = 1000,
  filters = [],
  checkUser = true,
  userId = "",
  validationStep = null, // New parameter for validation step
}) => {
  const { searchByFilter } = MainServices();
  const [isSearching, setIsSearching] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);

  // Separate function to check if user is current employee
  const isCurrentEmployee = useCallback(
    (recordUserId) => {
      console.log("record id & userID: ", recordUserId, userId);
      // For checkUser true: compare userId with userId
      if (recordUserId && userId && recordUserId === userId) {
        return true;
      }

      return false;
    },
    [userId]
  );

  // Separate function to handle user-specific validation
  const handleUserValidation = useCallback(
    (records) => {
      if (!records || records.length === 0) {
        return { isValid: true, message: "", validationStep };
      }

      const firstRecord = records[0];

      // Check if this is the current employee's data
      if (isCurrentEmployee(firstRecord.userId)) {
        return { isValid: true, message: "", validationStep };
      }

      // Check user type from the nested user object
      const userType = firstRecord.user?.type;
      const validUserTypes = ["user", "master", "admin"];

      // Only send validation message if user type is one of the valid types
      if (!validUserTypes.includes(userType)) {
        return { isValid: true, message: "", validationStep };
      }

      // Check user status from the nested user object
      const userStatus = firstRecord.user?.status || "unknown";

      const message = generateValidationMessage(
        firstRecord,
        userStatus,
        columnName
      );

      return {
        isValid: false,
        message: message || "This field value is already in use.",
        status: userStatus,
        record: firstRecord,
        validationStep, // Include validation step in result
      };
    },
    [isCurrentEmployee, columnName, validationStep]
  );

  const performSearch = useCallback(
    async (inputValue) => {
      if (!inputValue || !inputValue.trim()) {
        return { isValid: true, message: "", validationStep };
      }

      if (!endpoint || !columnName) {
        return { isValid: true, message: "", validationStep };
      }

      setIsSearching(true);
      setError(null);

      try {
        let filter;

        if (endpoint === apiServices?.getEmployeePersonalInfo) {
          // If filters are provided, use them along with required status and columnName filters
          filter = [
            {
              key: "status",
              in: ["active", "inactive", "draft"],
            },
            {
              key: "type",
              in: ["user", "master", "admin"],
            },
            {
              key: columnName,
              eq: inputValue.trim(),
            },
            ...filters,
          ];
        } else {
          // Use default filters if none provided
          filter = [
            {
              key: "status",
              in: ["active", "inactive", "draft"],
            },
            {
              key: columnName,
              eq: inputValue.trim(),
            },
            ...filters,
          ];
        }

        const response = await searchByFilter(endpoint, filter);
        const records = safelyGetRecords(response);

        if (!records || records.length === 0) {
          return { isValid: true, message: "", validationStep };
        }

        // Handle user-specific validation when checkUser is true
        if (checkUser) {
          return handleUserValidation(records);
        }

        // Original validation logic for non-user checks
        const firstRecord = records[0];

        // Check if this is the current employee's data (using publicId for checkUser false)
        const recordPublicId = firstRecord?.personalInfo?.publicId;
        if (isCurrentEmployee(recordPublicId)) {
          return { isValid: true, message: "", validationStep };
        }

        let status = "unknown";

        if (endpoint === apiServices?.getEmployeePersonalInfo) {
          status = firstRecord?.personalInfo?.[statusField] || "unknown";
        } else {
          status = firstRecord[statusField] || "unknown";
        }

        const message = generateValidationMessage(
          firstRecord,
          status,
          columnName
        );

        const result = {
          isValid: false,
          message: message || "This field value is already in use.",
          status: status,
          record: firstRecord,
          validationStep, // Include validation step in result
        };

        return result;
      } catch (err) {
        const errorMessage = err && err.message ? err.message : "Search failed";
        setError(errorMessage);
        return { isValid: true, message: "", validationStep };
      } finally {
        setIsSearching(false);
      }
    },
    [
      endpoint,
      columnName,
      statusField,
      filters,
      checkUser,
      handleUserValidation,
      validationStep,
    ]
  );

  const debouncedSearch = useCallback(
    deBounce(async (inputValue) => {
      const result = await performSearch(inputValue);
      setValidationResult(result);
    }, debounceMs),
    [performSearch, debounceMs]
  );

  const clearValidation = useCallback(() => {
    setValidationResult(null);
    setError(null);
  }, []);

  return {
    debouncedSearch,
    isSearching,
    validationResult,
    error,
    clearValidation,
    validationStep, // Expose validation step
  };
};
